/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef } from 'react';
import { addMinutes, format } from 'date-fns';

import Icon from '../../icon/Icon';
import TimePicker from './time-picker/TimePicker';
import { usePrevious } from '../../../core/hooks/usePrevious';
import { getTimeDate } from '../../../core/utils/date';

import './RangeTimePicker.scss';

const HOURS_MINUTES_FORMAT = 'HH:mm';

interface Range {
  firstValue: Date;
  secondValue: Date;
}

interface Props {
  id?: string;
  label?: string;
  firstValue: Date;
  secondValue: Date;
  firstName?: string;
  secondName?: string;
  onChange: (range: Range) => void;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  excludeDisablingHours?: boolean;
  disableBeforeValue?: Date;
  disableAfterValue?: Date;
  deliveryDurationMin?: number;
  deliveryDurationMax?: number;
}

const RangeTimePicker: React.FC<Props> = ({
  id,
  label,
  firstValue,
  secondValue,
  firstName,
  secondName,
  onChange,
  required,
  error,
  disabled,
  disableBeforeValue,
  disableAfterValue,
  excludeDisablingHours = false,
  deliveryDurationMin,
  deliveryDurationMax
}) => {
  const [firstDate, setFirstDate] = React.useState<Date>(firstValue);
  const [secondDate, setSecondDate] = React.useState<Date>(secondValue);
  const rangeRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const prevFirstValue = usePrevious(firstValue);
  const prevSecondValue = usePrevious(secondValue);

  useEffect(() => {
    if (prevFirstValue !== firstValue) {
      setFirstDate(firstValue);
    }
    if (prevSecondValue !== secondValue) {
      setSecondDate(secondValue);
    }
  }, [prevFirstValue, firstValue, prevSecondValue, secondValue]);

  return (
    <div className={`range-time-picker ${isFocused ? 'range-time-picker--is-focused' : ''} ${error ? 'range-time-picker--error' : ''}`}>
      {label && (
        <label className="range-time-picker__label" htmlFor={`${id}-1`}>
          {label}
          {required && '*'}
        </label>
      )}
      <div className={`range-time-picker__input ${disabled ? 'range-time-picker__input--disabled' : ''}`} ref={rangeRef}>
        <TimePicker
          id={`${id}-1`}
          name={firstName}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          excludeDisablingHours={excludeDisablingHours}
          disableBefore={format(disableBeforeValue || getTimeDate('00:00'), HOURS_MINUTES_FORMAT)}
          disableAfter={format(disableAfterValue || getTimeDate('24:00'), HOURS_MINUTES_FORMAT)}
          onChange={(value) => {
            setFirstDate(value);
            onChange({
              firstValue: value,
              secondValue: secondDate
            });

            if (rangeRef.current) {
              const secondInput = rangeRef.current.querySelectorAll<HTMLInputElement>('.time-picker__input');
              if (secondInput[1]) {
                secondInput[1].focus();
              }
            }
          }}
          value={format(firstDate, HOURS_MINUTES_FORMAT)}
          placeholder="00:00"
        />
        <span className="range-time-picker__minus">
          <Icon icon="minus" size="full-size" svg />
        </span>
        <TimePicker
          id={`${id}-2`}
          name={secondName}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          excludeDisablingHours={excludeDisablingHours}
          onChange={(value) => {
            setSecondDate(value);
            onChange({
              firstValue: firstDate,
              secondValue: value
            });
          }}
          value={format(secondDate, HOURS_MINUTES_FORMAT)}
          placeholder="00:00"
          disableBefore={format(
            deliveryDurationMin
              ? addMinutes(firstDate, deliveryDurationMin)
              : disableBeforeValue && disableBeforeValue > firstDate
              ? addMinutes(disableBeforeValue, 30)
              : addMinutes(firstDate, 30),
            HOURS_MINUTES_FORMAT
          )}
          disableAfter={format(
            deliveryDurationMax
              ? disableAfterValue && disableAfterValue < addMinutes(firstDate, deliveryDurationMax)
                ? disableAfterValue
                : addMinutes(firstDate, deliveryDurationMax)
              : disableAfterValue || secondValue,
            HOURS_MINUTES_FORMAT
          )}
        />
        <div className="range-time-picker__icon">
          <Icon icon="clock" size="full-size" svg />
          <Icon
            icon="close"
            size="full-size"
            svg
            onClick={() => {
              onChange({
                firstValue: getTimeDate('06:00'),
                secondValue: getTimeDate('17:00')
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default RangeTimePicker;
